import React from 'react';
import PropTypes from 'prop-types';

import { formatNumberWithSeparators } from '@tradera/blueprint/utils';
import MultiCurrency from 'tradera-components/multi-currency/multi-currency';
import ITEM_TYPES from 'tradera-constants/item-types';
import { useTranslator } from 'tradera-lang';
import { formatDate } from 'tradera-utils/date';
import TimeLeft from '../helpers/time-left';
import { ITEM_STATES } from 'tradera-state/views/view-item/constants';

const isSupportedItemType = itemType =>
    [ITEM_TYPES.AUCTION, ITEM_TYPES.AUCTION_BIN].includes(itemType);

function BidDetails(props) {
    const {
        bidCount,
        className,
        endDate,
        fixedPrice,
        hasEnded,
        hasLeadingBid,
        hasLostLeadingBid,
        isCountdownEnabled,
        isEnding,
        itemState,
        itemType,
        leadingBidAmount,
        onShowBids,
        openingBid,
        preferredCurrency,
        secondsLeft,
        serverSecondsLeft
    } = props;
    const { t } = useTranslator();

    if (!isSupportedItemType(itemType)) {
        return null;
    }

    const hasBids = bidCount > 0;
    const amountDetails = getAmountDetails(
        t,
        itemState,
        fixedPrice,
        leadingBidAmount,
        openingBid,
        hasBids
    );

    return (
        <section className={`d-flex justify-content-between ${className}`}>
            <div className="mr-2">
                <p
                    data-test-bid-details-amount-title=""
                    className="text-nowrap text-gray-600 mb-2">
                    {amountDetails.title}
                </p>
                <h4
                    data-test-bid-details-amount=""
                    className={`text-nowrap ${
                        hasLeadingBid ? 'text-success' : ''
                    }`}>
                    {formatNumberWithSeparators(amountDetails.amount)}{' '}
                    {t('vip_currency')}
                    <br />
                    <MultiCurrency
                        amountInSek={amountDetails.amount}
                        className="font-weight-normal"
                        preferredCurrency={preferredCurrency}
                    />
                </h4>
            </div>
            {isEnding || hasEnded ? (
                <div className="mr-2">
                    <p
                        data-test-ended-time=""
                        className="text-nowrap text-gray-600 mb-2">
                        {t('vip_hasEndedTime')} {formatDate(new Date(endDate))}
                    </p>
                    <h4
                        className={isEnding ? 'text-danger' : ''}
                        data-test-time-ended="">
                        {hasEnded ? t('vip_ended') : t('vip_ending')}
                    </h4>
                </div>
            ) : (
                <div className="mr-2">
                    <p
                        data-test-will-end-time=""
                        className="text-nowrap text-gray-600 mb-2">
                        {t('vip_willEndTime')} {formatDate(new Date(endDate))}
                    </p>
                    <h4>
                        <TimeLeft
                            seconds={secondsLeft}
                            serverSecondsLeft={serverSecondsLeft}
                            isCountdownEnabled={isCountdownEnabled}
                        />
                    </h4>
                </div>
            )}
            <div>
                <p className="text-gray-600 mb-2">
                    {t('vip_bid')}{' '}
                    {hasBids && (
                        <span>
                            (
                            <a
                                className="text-primary text-lowercase"
                                href="#"
                                onClick={e => {
                                    e.preventDefault();
                                    onShowBids();
                                }}>
                                <u>{t('vip_show')}</u>
                            </a>
                            )
                        </span>
                    )}
                </p>
                <h4
                    data-test-number-of-bids=""
                    className={`text-nowrap ${
                        hasLeadingBid || hasLostLeadingBid ? 'text-primary' : ''
                    }`}>
                    {bidCount} {t('vip_amount')}
                </h4>
            </div>
        </section>
    );
}

BidDetails.propTypes = {
    bidCount: PropTypes.number,
    className: PropTypes.string,
    endDate: PropTypes.string.isRequired,
    hasEnded: PropTypes.bool.isRequired,
    hasLeadingBid: PropTypes.bool.isRequired,
    hasLostLeadingBid: PropTypes.bool.isRequired,
    isCountdownEnabled: PropTypes.bool.isRequired,
    itemState: PropTypes.string.isRequired,
    leadingBidAmount: PropTypes.number,
    onShowBids: PropTypes.func,
    openingBid: PropTypes.number,
    secondsLeft: PropTypes.number,
    serverSecondsLeft: PropTypes.number
};

BidDetails.defaultProps = {
    className: '',
    hasLeadingBid: false,
    hasLostLeadingBid: false
};

export const getAmountDetails = (
    t,
    itemState,
    fixedPrice,
    leadingBid,
    openingBid,
    hasBids
) => {
    if (itemState === ITEM_STATES.ENDED_AUCTION_BIN_BOUGHT_AS_BIN) {
        return {
            title: t('vip_finalPrice'),
            amount: fixedPrice
        };
    }

    if (itemState === ITEM_STATES.ENDED_AUCTION_WITH_WINNING_BID) {
        return {
            title: t('vip_finalPrice'),
            amount: leadingBid
        };
    }

    if (hasBids) {
        return {
            title: t('vip_leadingBid'),
            amount: leadingBid
        };
    }
    return {
        title: t('vip_openingBid'),
        amount: openingBid
    };
};

export default BidDetails;
